// Copyright (c) 2026 PGNS LLC

import { RefreshCw } from 'lucide-react';
import { Button } from '@pgns/ui/button';
import type { Summary } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatRelative, STATUS_FILL, STATUS_TEXT } from '@/lib/status';

/**
 * The headline every reader came for, in the first screenful and in words.
 *
 * The description comes from the Worker rather than being re-derived here: the
 * JSON API and the page must never disagree about whether pgns is up.
 */
export function OverallStatus({
  summary,
  lastLoadedAt,
  now,
  onRefresh,
}: {
  summary: Summary;
  lastLoadedAt: number | null;
  now: number;
  onRefresh: () => void;
}) {
  const staleSeconds = lastLoadedAt === null ? null : Math.round((now - lastLoadedAt) / 1000);
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className={cn('size-3 rounded-full shrink-0', STATUS_FILL[summary.status])}
        />
        <div>
          <h1 className={cn('text-2xl font-semibold', STATUS_TEXT[summary.status])}>
            {summary.description}
          </h1>
          <p className="text-sm text-muted-foreground">
            Measured {formatRelative(summary.generated_at, now)}
            {staleSeconds !== null && staleSeconds > 120 ? ' · this page is not refreshing' : ''}
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="size-3.5" />
        Refresh
      </Button>
    </div>
  );
}
